<script lang="ts">
  import {fade} from 'svelte/transition'
  import DropdownWrapper from './DropdownWrapper.svelte'

  type Option = { label: string, value: string | number | boolean }
  export let value: string | number | undefined | boolean
  export let options: Option[]
  export let autocomplete = false
  export let onChange: ((v: string | number | undefined | boolean) => void) | undefined = undefined
  export let icon: string | false = 'icon-p-drop-arrow'
  export let titleClass = 'text'
  export let emptyOption: string | undefined = undefined
  export let prefix: string | undefined = undefined
  export let autoclose = true

  let searchInput: HTMLInputElement
  let search = ''
  let selectedIdx: number | null = null
  let active = false

  $: selectedOption = options.filter(o => o.value === value)[0]
  $: displayedOptions = buildDisplayedOptions(options, autocomplete, emptyOption, search)

  function buildDisplayedOptions(options: Option[], autocomplete: boolean,
                                 emptyOption: string | undefined, search: string): Option[] {
    let result = emptyOption ? [{label: emptyOption} as Option, ...options] : options
    if (autocomplete) {
      result = result.filter(o => {
        return (o.label ?? '').toLowerCase().includes(search.toLowerCase())
      })
    }
    return result
  }

  function focusSearch() {
    if (!searchInput) return
    searchInput.focus()
  }

  function onSelectWithKeyboard(e: KeyboardEvent, option: Option | null) {
    if (e.key === 'Enter') {
      value = option?.value
      if (onChange) onChange(value)
    }
  }

  function onSelectWithClick(option: Option | null) {
    value = option?.value
    if (onChange) onChange(value)
  }

  function onKeyDown(e: KeyboardEvent, show: boolean, toggle: (e: Event) => void, close: () => void) {
    switch (e.key) {
      case 'ArrowDown':
        return selectedIdx = (selectedIdx != undefined ? (selectedIdx + 1) % displayedOptions.length : 0)
      case 'ArrowUp':
        return selectedIdx = (selectedIdx ? selectedIdx - 1 : displayedOptions.length - 1)
      case 'Enter': {
        e.preventDefault()
        onSelectWithClick(displayedOptions[selectedIdx!])
        search = ''
        selectedIdx = null
        if (autoclose) close()
        return
      }
      default: {
        if (!show) toggle(e)
      }
    }
  }

  function onClickPrefix(e: Event, toggle: (e: Event) => void) {
    toggle(e)
    focusSearch()
  }
</script>

<DropdownWrapper {autoclose} let:show let:close let:toggle
                 class="{prefix ? ' ' : ($$props.class ?? `ui dropdown select ${autocomplete ? 'search inline' : ''} ${active ? 'active visible' : ''}`)}">
  <svelte:fragment slot="prefix" let:toggle>
    <span onclick={e => onClickPrefix(e, toggle)}>
      <slot name="prefix" />
    </span>
  </svelte:fragment>

  {#if icon}
    <i class="dropdown icon {icon}" onclick={focusSearch}></i>
  {/if}

  {#if autocomplete}
    <input class="search" autocomplete="off" tabindex="0" bind:value={search} bind:this={searchInput}
           onkeydown={(e) => onKeyDown(e, show, toggle, close)}
           onblur={() => active = false}
           onfocus={() => active = true}/>
  {/if}

  <div class="{titleClass} {search ? 'filtered' : ''}" onclick={focusSearch}>
    {selectedOption?.label || (emptyOption ? emptyOption : '')}
  </div>

  {#if show}
    <div class="menu" transition:fade={{duration: 100}}>
      {#each displayedOptions as o, i}
        <div class="item {i === selectedIdx || o.value === value? 'active selected' : ''}"
             aria-roledescription="option"
             onclick={() => onSelectWithClick(o)}
             onkeydown={e => onSelectWithKeyboard(e, o)}>
          {o.label}
        </div>
      {/each}
    </div>
  {/if}
</DropdownWrapper>

<style>
  .menu {
    visibility: visible !important;
    display: block !important;
  }
</style>